import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AnimatedNumber } from "../components/AnimatedNumber";
import {
  ACCEPT_METHODS,
  acceptFee,
  acceptedMethods,
  customerAccepts,
  feeIsSpiked,
  flexibilityOf,
  formatFee,
  formatUsd,
  methodAvailable,
  methodLabel,
  resolveSale,
} from "./engine";
import type { AcceptMethod, Customer, HustleResult, RoundConditions } from "./types";

interface HustleScreenProps {
  customer: Customer;
  conditions: RoundConditions;
  result: HustleResult;
  progress: { round: number; totalRounds: number };
  onAccept: (method: AcceptMethod) => void;
}

export function HustleScreen({ customer, conditions, result, progress, onAccept }: HustleScreenProps) {
  const [revealed, setRevealed] = useState<AcceptMethod | null>(null);
  const accepted = acceptedMethods(customer);
  const flexibility = flexibilityOf(customer);

  function proceed() {
    if (revealed) {
      onAccept(revealed);
      setRevealed(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Round progress */}
      <div className="w-full">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-muted-foreground">
            Customer {progress.round} of {progress.totalRounds}
          </span>
          <span className="text-xs text-muted-foreground">Your Shop 🏪</span>
        </div>
        <div className="flex gap-1" role="progressbar" aria-valuemin={1} aria-valuemax={progress.totalRounds} aria-valuenow={progress.round}>
          {Array.from({ length: progress.totalRounds }, (_, i) => (
            <div
              key={i}
              className={`h-2 flex-1 rounded-full transition-colors ${
                i < progress.round - 1
                  ? "bg-primary"
                  : i === progress.round - 1
                    ? "bg-primary/40"
                    : "bg-muted"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Live tally */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <Tally label="Net" number={result.netUsd} fmt={formatUsd} accent />
        <Tally label="Fees" number={result.feesUsd} fmt={formatFee} muted />
        <Tally label="🌱 Converts" number={result.bchConverts} fmt={(n) => `${Math.round(n)}`} accent />
        <Tally label="Rep" number={result.reputation} fmt={(n) => `${Math.round(n)}`} />
      </div>

      {/* Network conditions */}
      <div
        className={`text-[11px] text-center rounded-lg px-3 py-2 ${
          conditions.btcCongested
            ? "bg-destructive/10 text-destructive font-medium"
            : "bg-muted/60 text-muted-foreground"
        }`}
      >
        {conditions.note}
      </div>

      {/* Customer card — re-animates for each new customer */}
      <div key={customer.id} className="flex flex-col gap-4 animate-screen-enter">
      <Card className="overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-4xl" aria-hidden>{customer.icon}</div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <Badge variant="secondary">{customer.name}</Badge>
                {flexibility === "binding" ? (
                  <Badge variant="destructive" className="text-[10px]">⚠ won't budge</Badge>
                ) : flexibility === "firm" ? (
                  <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    set on {methodLabel(customer.prefers)}
                  </Badge>
                ) : (
                  <Badge className="text-[10px] bg-primary/15 text-primary">open-minded</Badge>
                )}
              </div>
              <h2 className="text-xl font-bold leading-tight text-foreground">
                {customer.item} · {formatUsd(customer.priceUsd)}
              </h2>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{customer.flavor}</p>
          {flexibility === "binding" ? (
            <p className="text-xs mt-2 rounded-lg bg-destructive/10 text-destructive font-medium px-3 py-2">
              🚫 Will only pay with{" "}
              <span className="font-bold">
                {accepted.map((m) => methodLabel(m)).join(" or ")}
              </span>{" "}
              — offer anything else and they walk out. No sale.
            </p>
          ) : flexibility === "firm" ? (
            <p className="text-xs mt-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium px-3 py-2">
              Prefers <span className="font-bold">{methodLabel(customer.prefers)}</span>, but you can
              steer them — they'll grumble a little (a BCH conversion still wins them over).
            </p>
          ) : (
            <p className="text-xs mt-2 rounded-lg bg-primary/10 text-primary font-medium px-3 py-2">
              Reaches for <span className="font-bold">{methodLabel(customer.prefers)}</span>, but
              they're happy to try anything — steer them to BCH and everyone wins.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Accept choices or outcome */}
      {!revealed ? (
        <div className="flex flex-col gap-2.5">
          {ACCEPT_METHODS.filter((m) => methodAvailable(m, customer)).map((method) => (
            <AcceptButton
              key={method}
              method={method}
              customer={customer}
              conditions={conditions}
              onClick={() => setRevealed(method)}
            />
          ))}
          <button
            onClick={() => setRevealed("decline")}
            className="w-full text-left rounded-xl border-2 border-dashed border-border bg-transparent px-4 py-3 text-sm text-muted-foreground transition-all hover:border-destructive/50 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]"
          >
            Turn the customer away
          </button>
        </div>
      ) : (
        <OutcomeCard customer={customer} method={revealed} conditions={conditions} onNext={proceed} />
      )}
      </div>
    </div>
  );
}

function AcceptButton({
  method,
  customer,
  conditions,
  onClick,
}: {
  method: AcceptMethod;
  customer: Customer;
  conditions: RoundConditions;
  onClick: () => void;
}) {
  const fee = acceptFee(method, customer.priceUsd, conditions);
  const spiked = feeIsSpiked(method, conditions);
  const net = Math.round((customer.priceUsd - fee) * 100) / 100;
  const matchesPref = method === customer.prefers;
  const refuses = !customerAccepts(customer, method);
  const flexibility = flexibilityOf(customer);

  // Steering signals: a BCH conversion is a smart move; firm customers grumble
  // when steered to a non-BCH method.
  const steer = !matchesPref && !refuses;
  const suggestBch = steer && method === "bch";
  const grumbles = steer && !suggestBch && flexibility === "firm";

  const note = methodNote(method, fee, spiked);
  const risky = method === "card";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99] ${
        refuses
          ? "border-dashed border-destructive/30 bg-destructive/5 hover:border-destructive/60"
          : "border-border bg-card hover:border-primary hover:bg-primary/5"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold ${refuses ? "text-muted-foreground" : "text-foreground"}`}>
              Accept {methodLabel(method)}
            </span>
            {refuses ? (
              <Badge variant="destructive" className="text-[10px]">walks out</Badge>
            ) : (
              <>
                {matchesPref && (
                  <Badge className="text-[10px] bg-primary/15 text-primary">preferred</Badge>
                )}
                {suggestBch && (
                  <Badge className="text-[10px] bg-primary/15 text-primary">👍 suggest BCH</Badge>
                )}
                {grumbles && (
                  <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    they'll grumble
                  </Badge>
                )}
                {risky && (
                  <Badge variant="destructive" className="text-[10px]">chargeback risk</Badge>
                )}
              </>
            )}
          </div>
          <p className={`text-xs mt-0.5 ${refuses ? "text-destructive" : spiked ? "text-destructive font-medium" : "text-muted-foreground"}`}>
            {refuses ? "This customer refuses this method — no sale." : note}
          </p>
        </div>
        <div className="text-right shrink-0">
          {refuses ? (
            <p className="font-bold text-muted-foreground">+$0.00</p>
          ) : (
            <>
              <p className={`font-bold ${method === "bch" ? "text-primary" : "text-foreground"}`}>
                +{formatUsd(net)}
              </p>
              {net < customer.priceUsd && (
                <p className="text-[11px] text-muted-foreground line-through">
                  {formatUsd(customer.priceUsd)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function methodNote(method: AcceptMethod, fee: number, spiked: boolean): string {
  switch (method) {
    case "bch":
      return "instant · fee <$0.01 · final, no chargebacks";
    case "btc-lightning":
      return "fast · tiny fee · may fail to route";
    case "btc-onchain":
      return `− ${formatFee(fee)} network fee${spiked ? " ⚠️ spike!" : ""} · settles on-chain`;
    case "card":
      return `− ${formatFee(fee)} processor fee · reversible for weeks`;
    case "decline":
      return "";
  }
}

function OutcomeCard({
  customer,
  method,
  conditions,
  onNext,
}: {
  customer: Customer;
  method: AcceptMethod;
  conditions: RoundConditions;
  onNext: () => void;
}) {
  const sale = resolveSale(customer, method, conditions);
  const good = sale.completed && sale.chargebackUsd === 0;
  const icon = !sale.completed ? "⚠️" : sale.chargebackUsd > 0 ? "💸" : method === "bch" ? "🟢" : "✅";

  // Dramatic bad moments: failed sale, a chargeback, or a spiked fee.
  const alarming = !sale.completed || sale.chargebackUsd > 0 || (sale.feeSpiked && sale.feeUsd >= 5);

  return (
    <>
      {alarming && (
        <div
          className="pointer-events-none fixed inset-0 z-40 bg-destructive animate-danger-flash"
          aria-hidden
        />
      )}
      <Card
        className={`animate-pop-in ${alarming ? "animate-danger-shake " : ""}${
          good ? "border-primary/50" : "border-destructive/50"
        }`}
      >
      <CardContent className="p-5 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="text-3xl" aria-hidden>{icon}</div>
          <p className="text-sm text-foreground leading-relaxed">{sale.outcome}</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-lg bg-muted/60 px-2 py-1.5">
            <p className="text-[10px] uppercase text-muted-foreground">Kept</p>
            <p className="font-bold text-primary">{formatUsd(sale.netUsd)}</p>
          </div>
          <div className="rounded-lg bg-muted/60 px-2 py-1.5">
            <p className="text-[10px] uppercase text-muted-foreground">Fee</p>
            <p className="font-bold text-foreground">{formatFee(sale.feeUsd)}</p>
          </div>
          <div className="rounded-lg bg-muted/60 px-2 py-1.5">
            <p className="text-[10px] uppercase text-muted-foreground">Rep</p>
            <p className={`font-bold ${sale.reputationDelta >= 0 ? "text-primary" : "text-destructive"}`}>
              {sale.reputationDelta >= 0 ? "+" : ""}{sale.reputationDelta}
            </p>
          </div>
        </div>
        <Button className="w-full h-12 font-bold" onClick={onNext}>
          Next Customer
        </Button>
      </CardContent>
      </Card>
    </>
  );
}

function Tally({
  label,
  number,
  fmt,
  accent,
  muted,
}: {
  label: string;
  number: number;
  fmt: (n: number) => string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`rounded-lg px-1.5 py-2 ${accent ? "bg-primary/10" : "bg-muted/70"}`}>
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-sm font-bold ${accent ? "text-primary" : muted ? "text-muted-foreground" : "text-foreground"}`}>
        <AnimatedNumber value={number} format={fmt} />
      </p>
    </div>
  );
}
